import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Stars as StarsIcon,
  Psychology as PsychologyIcon,
  Diamond as DiamondIcon,
  Home as HomeIcon,
  Favorite as FavoriteIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

const Home: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    { icon: <StarsIcon />, title: 'Accurate Predictions', description: 'Precise insights based on ancient Vedic astrology principles.' },
    { icon: <PsychologyIcon />, title: 'Personal Guidance', description: 'Personalized advice for career, relationships, and life decisions.' },
    { icon: <DiamondIcon />, title: 'Gemstone Consultation', description: 'Discover gemstones to enhance your positive energies.' },
    { icon: <HomeIcon />, title: 'Vastu Advice', description: 'Optimize living and working spaces for prosperity.' },
    { icon: <FavoriteIcon />, title: 'Compatibility Check', description: 'Detailed compatibility analysis for harmonious relationships.' },
    { icon: <ScheduleIcon />, title: 'Flexible Timing', description: 'Convenient time slots that fit your schedule.' },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, sm: 3 } }}>
      {/* Hero */}
      <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 }, pt: { xs: 2, md: 4 } }}>
        <Typography variant="h1" sx={{ mb: 1 }}>
          Welcome to Amrutha Kalasam
        </Typography>
        <Typography sx={{ fontSize: '1.6rem', color: 'secondary.main', mb: 1, opacity: 0.5 }}>꩜</Typography>
        <Typography variant="h5" sx={{ mb: 3, color: 'text.secondary', maxWidth: 500, mx: 'auto' }}>
          Guiding You Towards a Prosperous Future
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
          <Button variant="contained" size="large" onClick={() => navigate('/book-slot')}>
            Book Consultation
          </Button>
          <Button variant="outlined" size="large" onClick={() => navigate('/services')}>
            View Services
          </Button>
        </Box>
        {/* Trust stats */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 3, md: 5 }, flexWrap: 'wrap' }}>
          {[
            { value: '30+', label: 'Years Experience' },
            { value: '10K+', label: 'Happy Clients' },
            { value: '95%', label: 'Accuracy Rate' },
          ].map((s) => (
            <Box key={s.label} sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700 }}>{s.value}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* About Vedic Astrology */}
      <Box sx={{ mb: { xs: 5, md: 7 } }}>
        <Typography variant="h3" sx={{ textAlign: 'center', mb: 3 }}>
          The Power of Vedic Astrology
        </Typography>
        <Card sx={{ p: { xs: 2, md: 3 } }}>
          <CardContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Vedic astrology (Jyotish) is one of the oldest astrological systems, originating from
              the Indian subcontinent over 5,000 years ago. Using the sidereal zodiac based on actual
              star positions, it provides incredibly accurate insights into personality, life events,
              and future possibilities.
            </Typography>
            <Typography variant="body1">
              Through careful analysis of your birth chart (Kundali), we provide guidance on career,
              marriage, health, finance, and spiritual growth — combining ancient wisdom with practical
              advice to help you navigate life with confidence.
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* About Astrologer */}
      <Card sx={{ mb: { xs: 5, md: 7 }, p: { xs: 2, md: 3 } }}>
        <CardContent>
          <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'center' }}>
            <Typography variant="h4" sx={{ mb: 1, color: 'primary.main' }}>
              Meet Your Astrologer
            </Typography>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Acharya Malyala Srinivas
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label="MA Astrology" color="primary" size="small" />
              <Chip label="30+ Years" color="secondary" size="small" />
              <Chip label="Pranic Healing" color="primary" size="small" />
              <Chip label="Vastu Expert" color="secondary" size="small" />
            </Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              A distinguished Vedic scholar with 30+ years of expertise. He holds an MA in Astrology
              from Potti Sreeramulu University, Hyderabad (2000), and masterfully blends ancient wisdom
              with modern insights.
            </Typography>
            <Box sx={{ display: 'grid', gap: 0.8, maxWidth: 480, mx: 'auto', textAlign: 'left', mb: 2 }}>
              {[
                '🔮 Vedic Astrology & Horoscope Analysis',
                '✨ Pranic Healing & Distant Healing (20+ years)',
                '🏠 Vastu Shastra Consultation (25+ years)',
                '⏰ Muhurta & Prashna/Horary Astrology',
                '🔍 Pendulum Dowsing & Tantra (30+ years)',
              ].map((s) => (
                <Typography key={s} variant="body2">{s}</Typography>
              ))}
            </Box>
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              "Let ancient wisdom illuminate your path to balance, clarity, and prosperity."
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Testimonials */}
      <Box sx={{ mb: { xs: 5, md: 7 } }}>
        <Typography variant="h3" sx={{ textAlign: 'center', mb: 1 }}>
          What Our Clients Say
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 4, color: 'text.secondary' }}>
          Trusted by thousands worldwide for over 30 years
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
          {[
            {
              text: 'Best Astrologer for me from past 19 years. As I moved to USA, he is helping me with online consultation. His predictions are much precise. Must visit Astrologer Srinivas Malyala Garu. If you want to solve your problems, you can blindly trust him.',
              name: 'Pavana Swetha',
              location: '🇺🇸 USA',
            },
            {
              text: 'Malyala Srinivas garu, a multi talented noble person stands out as one of the Best Astrologer in India. I know him since decades. He is wise and genuine astrologer. His predictions go right with the time. He guides the clients with his inspirational speeches and practical remedies.',
              name: 'Hariprasad S',
              location: '🇮🇳 India',
            },
          ].map((t) => (
            <Card key={t.name} sx={{ p: { xs: 2, md: 3 } }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 2 }}>
                  "{t.text}"
                </Typography>
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 0.5 }}>
                  {t.name}
                </Typography>
                <Chip label={t.location} size="small" variant="outlined" />
                <Box sx={{ mt: 1 }}>{'⭐'.repeat(5)}</Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Trust indicators removed — now in hero */}
      </Box>

      {/* Features */}
      <Box sx={{ mb: { xs: 5, md: 7 } }}>
        <Typography variant="h3" sx={{ textAlign: 'center', mb: 4 }}>
          Why Choose Our Services
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 2.5 }}>
          {features.map((f) => (
            <Card key={f.title} sx={{ p: 2, textAlign: 'center' }}>
              <CardContent>
                <Box sx={{ mb: 1.5, color: 'secondary.main', '& .MuiSvgIcon-root': { fontSize: 36 } }}>
                  {f.icon}
                </Box>
                <Typography variant="h5" sx={{ mb: 1 }}>{f.title}</Typography>
                <Typography variant="body2">{f.description}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      {/* CTA */}
      <Card sx={{ p: { xs: 3, md: 4 }, textAlign: 'center', backgroundColor: '#FFF5EB' }}>
        <CardContent>
          <Typography variant="h4" sx={{ mb: 2 }}>
            Ready to Discover Your Path?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Book your consultation today and take the first step towards understanding your destiny.
          </Typography>
          <Button variant="contained" size="large" onClick={() => navigate('/book-slot')}>
            Schedule Your Consultation
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Home;
